import Navbar from './home/components/Navbar';
import Footer from './home/components/Footer';
import RegisterLink from '../components/RegisterLink';
import { useLanguage } from '../contexts/LanguageContext';
import { usePageSeo } from '../hooks/usePageSeo';
import { LinkedText } from '../components/LinkedText';
import gamesHeroDragonFortune from '../assets/games-hero-dragon-fortune.png';
import provider918Kiss from '../assets/game-providers/918-kiss.png';
import providerAceWin from '../assets/game-providers/ace-win.png';
import providerAce333 from '../assets/game-providers/ace333.png';
import providerCq9 from '../assets/game-providers/cq9-gaming.png';
import providerEpicWin from '../assets/game-providers/epic-win.png';
import providerJili from '../assets/game-providers/jili.png';
import providerJoker from '../assets/game-providers/joker.png';
import providerLive22 from '../assets/game-providers/live22.png';
import providerMegaH5 from '../assets/game-providers/mega-h5.png';
import providerMicrogaming from '../assets/game-providers/microgaming.png';
import providerPegasus from '../assets/game-providers/pegasus.png';
import providerPlaytech from '../assets/game-providers/playtech.png';
import providerRichGaming from '../assets/game-providers/rich-gaming.png';
import providerScr888 from '../assets/game-providers/scr888-r5.png';
import providerUUSlot from '../assets/game-providers/uu-slot.png';
import providerVpower from '../assets/game-providers/vpower.png';
import providerWfGaming from '../assets/game-providers/wf-gaming.png';

const providers = [
  { name: 'Jili', logo: providerJili },
  { name: 'VPower', logo: providerVpower },
  { name: 'MegaH5', logo: providerMegaH5 },
  { name: 'Rich Gaming', logo: providerRichGaming },
  { name: 'WF Gaming', logo: providerWfGaming },
  { name: 'SCR888 H5', logo: providerScr888 },
  { name: 'Epic Gaming', logo: providerEpicWin },
  { name: 'ACE333', logo: providerAce333 },
  { name: 'Joker', logo: providerJoker },
  { name: '918Kiss', logo: provider918Kiss },
  { name: 'UU Slot', logo: providerUUSlot },
  { name: 'Live22', logo: providerLive22 },
  { name: 'Pegasus', logo: providerPegasus },
  { name: 'Playtech', logo: providerPlaytech },
  { name: 'Micrograming', logo: providerMicrogaming },
  { name: 'Pragmatic Play (via featured lobby)', logo: providerAceWin },
  { name: 'CQ9 Gaming (featured)', logo: providerCq9 },
];

export default function GamesPage() {
  const { lang, t } = useLanguage();
  const title = t(
    'MrCuci Games Malaysia | Online Slots, Live Casino & Fishing',
    'Permainan MrCuci Malaysia | Slot, Kasino Langsung & Memancing'
  );
  const description = t(
    'Explore MrCuci Malaysia casino games from Jili, VPower, MegaH5, Rich Gaming, 918Kiss, Live22 and more. Play slots, live casino and fishing games in MYR.',
    'Terokai permainan kasino MrCuci Malaysia daripada Jili, VPower, MegaH5, Rich Gaming, 918Kiss, Live22 dan banyak lagi. Main slot, kasino langsung dan permainan memancing dalam MYR.'
  );

  usePageSeo({
    title,
    description,
    path: '/games/',
    language: lang === 'bm' ? 'ms-MY' : 'en-MY',
    keywords: [
      'MrCuci games',
      'MrCuci slot games',
      'online slot Malaysia',
      'Malaysia casino games',
      'live casino Malaysia',
      'fishing games Malaysia',
      'Jili slot Malaysia',
      '918Kiss Malaysia',
      'VPower games Malaysia',
      'MegaH5 Malaysia',
      'mobile casino games Malaysia',
      'permainan kasino dalam talian Malaysia',
    ],
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': 'https://www.mrcuci.me/games/#webpage',
      url: 'https://www.mrcuci.me/games/',
      name: title,
      description,
      inLanguage: lang === 'bm' ? 'ms-MY' : 'en-MY',
      isPartOf: { '@id': 'https://www.mrcuci.me/#website' },
      mainEntity: {
        '@type': 'ItemList',
        name: t('MrCuci Game Providers', 'Pembekal Permainan MrCuci'),
        numberOfItems: providers.length,
        itemListElement: providers.map((provider, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: provider.name,
        })),
      },
    },
  });

  return (
    <div className="min-h-screen bg-[#080000] font-sans">
      <Navbar />

      <main className="pt-20">
        <section
          className="py-12 md:py-16 border-b border-[#d4a017]/10"
          style={{ background: 'linear-gradient(135deg, #0d0000 0%, #1a0303 55%, #0d0000 100%)' }}
        >
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-[#d4a017] text-xs md:text-sm font-semibold tracking-widest uppercase mb-4">
                {t('MrCuci Games & Providers', 'Permainan & Pembekal MrCuci')}
              </p>
              <h1 className="text-3xl md:text-5xl font-black text-white leading-tight mb-4">
                <LinkedText>{t(
                  'Malaysia Online Casino Game Providers at MrCuci',
                  'Pembekal Permainan Kasino Dalam Talian Malaysia di MrCuci'
                )}</LinkedText>
              </h1>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-4">
                <LinkedText>{t(
                  'Explore the full MrCuci provider lineup with trusted names such as Jili, VPower, MegaH5, Rich Gaming, WF Gaming, SCR888 H5, Epic Gaming, ACE333, Joker, 918Kiss, UU Slot, Live22, Pegasus, Playtech, Micrograming, and Pragmatic Play. Every provider card below links directly to our active gaming lobby.',
                  'Terokai senarai penuh pembekal MrCuci dengan nama-nama terpercaya seperti Jili, VPower, MegaH5, Rich Gaming, WF Gaming, SCR888 H5, Epic Gaming, ACE333, Joker, 918Kiss, UU Slot, Live22, Pegasus, Playtech, Micrograming, dan Pragmatic Play. Setiap kad pembekal di bawah akan membawa anda terus ke lobi permainan aktif kami.'
                )}</LinkedText>
              </p>
              <RegisterLink
                source="games_page_open_lobby"
                label={t('Open MrCuci Game Lobby', 'Buka Lobi Permainan MrCuci')}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 font-bold px-6 py-3 rounded-lg text-sm"
                style={{ background: 'linear-gradient(135deg, #cc1100, #ff2200)', color: 'white' }}
              >
                <span>{t('Open MrCuci Game Lobby', 'Buka Lobi Permainan MrCuci')}</span>
                <i className="ri-external-link-line" />
              </RegisterLink>
            </div>

              <img
                src={gamesHeroDragonFortune}
                alt="MrCuci Dragon Fortune game promotion"
                className="w-full h-auto object-contain object-center"
              />
          </div>
        </section>

        <section className="py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-black text-white mb-6">
              {t('Featured Game Providers', 'Pembekal Permainan Pilihan')}
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 md:gap-4">
              {providers.map((provider) => (
                <RegisterLink
                  key={provider.name}
                  source={`games_page_provider_${provider.name.toLowerCase().replace(/\s+/g, '_')}`}
                  label={provider.name}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="group flex flex-col items-center gap-2"
                >
                  <img
                    src={provider.logo}
                    alt={`${provider.name} game provider`}
                    className="w-full h-auto object-contain rounded-lg transition-transform duration-300 group-hover:scale-105 group-hover:brightness-110"
                  />
                  <span className="inline-flex items-center gap-0.5 text-[#d4a017] text-xs font-semibold opacity-80 group-hover:opacity-100 group-hover:underline transition-opacity">
                    {t('Play Now', 'Main Sekarang')} <i className="ri-arrow-right-up-line text-xs" />
                  </span>
                </RegisterLink>
              ))}
            </div>
          </div>
        </section>

        <section
          className="py-12 md:py-16 border-y border-[#d4a017]/10"
          style={{ background: 'linear-gradient(180deg, #0d0101 0%, #120202 100%)' }}
        >
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <article className="space-y-6">
              <h2 className="text-2xl md:text-3xl font-black text-white">
                {t(
                  'Complete Guide: Choosing the Right MrCuci Provider in 2026',
                  'Panduan Lengkap: Memilih Pembekal MrCuci yang Tepat pada 2026'
                )}
              </h2>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                <LinkedText>{t(
                  'The Malaysian online casino market keeps growing because players now expect two things at the same time: fun gameplay and stable payment flow. At MrCuci, provider quality is the foundation of both. Instead of showing only one or two mainstream brands, the platform combines multiple engines and studios so players can choose different game styles based on volatility, feature depth, interface preference, and device performance. This provider diversity is one of the key reasons search users looking for trusted online casino providers in Malaysia continue to land on MrCuci.',
                  'Pasaran kasino dalam talian Malaysia terus berkembang kerana pemain kini mengharapkan dua perkara serentak: permainan yang menyeronokkan dan aliran pembayaran yang stabil. Di MrCuci, kualiti pembekal adalah asas kepada kedua-duanya. Berbanding hanya menampilkan satu atau dua jenama arus perdana, platform ini menggabungkan pelbagai enjin dan studio supaya pemain boleh memilih gaya permainan yang berbeza berdasarkan volatiliti, kedalaman ciri, keutamaan antara muka, dan prestasi peranti. Kepelbagaian pembekal inilah antara sebab utama pengguna yang mencari pembekal kasino dalam talian terpercaya di Malaysia terus mendarat di MrCuci.'
                )}</LinkedText>
              </p>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                <LinkedText>{t(
                  'For slot-focused players, names like Jili, VPower, MegaH5, Rich Gaming, WF Gaming, SCR888 H5, Epic Gaming, ACE333, Joker, 918Kiss, UU Slot, Live22, Pegasus, Playtech, Micrograming, and Pragmatic Play offer different reel behaviors, bonus structures, and round pacing. Some providers are known for fast rounds and clean layouts, while others focus on heavier animation, extended feature chains, or longer retention sessions. A strong provider mix helps players avoid one-dimensional gameplay and improve long-term session control.',
                  'Bagi pemain yang fokus pada slot, nama seperti Jili, VPower, MegaH5, Rich Gaming, WF Gaming, SCR888 H5, Epic Gaming, ACE333, Joker, 918Kiss, UU Slot, Live22, Pegasus, Playtech, Micrograming, dan Pragmatic Play menawarkan tingkah laku reel, struktur bonus, dan kadar pusingan yang berbeza. Sesetengah pembekal dikenali dengan pusingan pantas dan susun atur bersih, manakala yang lain mengutamakan animasi lebih berat, rantaian ciri yang panjang, atau sesi retensi yang lebih lama. Gabungan pembekal yang kukuh membantu pemain mengelakkan permainan satu dimensi dan meningkatkan kawalan sesi jangka panjang.'
                )}</LinkedText>
              </p>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                <LinkedText>{t(
                  'Another major advantage is cross-device stability. Most Malaysian users switch between desktop and mobile during the day, so games must load quickly and remain responsive on mixed network conditions. MrCuci provider pages are optimized for this practical behavior: cleaner loading flow, direct provider entry, and reduced friction from browsing to first spin. This is especially important for users searching terms like "fast online casino game providers Malaysia" or "best mobile slot provider online."',
                  'Kelebihan utama lain ialah kestabilan merentas peranti. Kebanyakan pengguna Malaysia bertukar antara desktop dan mudah alih sepanjang hari, maka permainan mesti memuatkan dengan cepat dan kekal responsif pada pelbagai keadaan rangkaian. Halaman pembekal MrCuci dioptimumkan untuk gelagat praktikal ini: aliran muatan yang lebih bersih, kemasukan pembekal terus, dan geseran yang lebih rendah dari penyemakan imbas hinggalah pusingan pertama. Ini amat penting bagi pengguna yang mencari terma seperti "pembekal permainan kasino dalam talian terpantas Malaysia" atau "pembekal slot mudah alih terbaik."'
                )}</LinkedText>
              </p>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                <LinkedText>{t(
                  "Payment compatibility also affects provider experience. Even when the game quality is strong, players will not stay if deposit and withdrawal flow is inconsistent. MrCuci aligns provider access with local payment methods including FPX online banking system, MEPS, Digi, Hotlink, Touch n' Go eWallet, and Grab Pay. This integration model makes it easier for users to move from funding to gameplay without platform switching, reducing session drop-off and improving practical usability.",
                  "Keserasian kaedah pembayaran turut mempengaruhi pengalaman pembekal. Walaupun kualiti permainan tinggi, pemain tidak akan kekal jika aliran deposit dan pengeluaran tidak konsisten. MrCuci menyelaraskan akses pembekal dengan kaedah pembayaran tempatan termasuk FPX online banking system, MEPS, Digi, Hotlink, Touch n' Go eWallet, dan Grab Pay. Model integrasi ini memudahkan pengguna beralih dari pembiayaan ke permainan tanpa perlu menukar platform, mengurangkan kadar tinggalan sesi dan meningkatkan kebolehgunaan praktikal."
                )}</LinkedText>
              </p>
              <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                {t(
                  'If you are new, start with two or three providers and compare round speed, feature frequency, and UI comfort before scaling session size. If you are already experienced, use this page as a fast provider directory to move directly into your preferred catalog. In both cases, the goal is the same: clearer choices, smoother entry, and better gaming flow under one trusted brand environment.',
                  'Jika anda baharu, mulakan dengan dua atau tiga pembekal dan bandingkan kelajuan pusingan, kekerapan ciri, serta keselesaan antara muka sebelum meningkatkan saiz sesi. Jika anda sudah berpengalaman, gunakan halaman ini sebagai direktori pembekal pantas untuk terus masuk ke katalog pilihan anda. Dalam kedua-dua kes, matlamatnya sama: pilihan yang lebih jelas, kemasukan yang lebih lancar, dan aliran permainan yang lebih baik dalam satu persekitaran jenama yang dipercayai.'
                )}
              </p>
            </article>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}
